import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { PlanStep } from "@/shared/contracts/proposal";
import { request } from "@/shared/http/client";

/**
 * O DOCUMENTO que entra e o que sai — importar, exportar, e o registo do que já
 * foi aplicado aqui.
 *
 * ── O QUE SAIU DAQUI, E PARA ONDE ─────────────────────────────────────────
 *
 * A vitrine (`useBlueprints`), o documento de um pacote e o "Aplicar" de um
 * clique eram desta feature enquanto a tela `/blueprints` existiu. Com o WP-C2
 * eles passaram a ser a aba "O que pode ter" de "Seu sistema", que aplica pela
 * PORTA ÚNICA depois de a pessoa ler a proposta — e vivem em
 * `features/system/api/use-system.ts`. Ficaram aqui as duas metades que
 * continuam a ser sobre um FICHEIRO: importar um documento de fora e exportar
 * peças deste workspace.
 */
export interface BlueprintOutcome {
	readonly code: string;
	readonly created: string[];
	readonly skipped: string[];
	readonly failed: Array<{ step: string; reason: string }>;
}

export interface BlueprintPreview {
	readonly code: string;
	readonly name: string;
	readonly delivers: readonly string[];
	readonly steps: readonly PlanStep[];
}

export interface AppliedBlueprint {
	readonly code: string;
	readonly version: string | null;
	readonly checksum: string;
	readonly outcome: BlueprintOutcome;
	readonly appliedAt: string;
}

/**
 * The diff, before anything is written.
 *
 * A read that travels as a POST, because the document is the request and a
 * blueprint does not fit on a query string.
 */
export function usePreviewBlueprint() {
	return useMutation({
		mutationFn: async (document: unknown) =>
			request<BlueprintPreview>("/v3/blueprints/preview", {
				method: "POST",
				body: document as Record<string, unknown>,
				timeoutMs: 60_000,
			}),
	});
}

export function useImportBlueprint() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (document: unknown) =>
			request<BlueprintOutcome>("/v3/blueprints/import", {
				method: "POST",
				body: document as Record<string, unknown>,
				timeoutMs: 180_000,
			}),
		onSuccess: () => client.invalidateQueries(),
	});
}

/**
 * As seis famílias que um documento carrega — a mesma lista do servidor.
 *
 * `schedules` saiu (05/09): um horário é uma automação com relógio, e viaja
 * dentro de `automations[]` com o `cron` e o `runAt` dele.
 */
export const EXPORT_SECTIONS = [
	"entities",
	"pipelines",
	"agents",
	"automations",
	"artifacts",
	"reports",
	// O site viaja como RASCUNHO, com as funções que a página chama (30/10).
	"sites",
] as const;

export type ExportSection = (typeof EXPORT_SECTIONS)[number];

/** Uma peça no inventário. `code` de uma automação é o ID dela: regra não tem código. */
export interface ExportableItem {
	readonly code: string;
	readonly name: string;
	/** Segunda linha curta: onde a regra escuta ou o relógio dela, a tela do funil. */
	readonly hint?: string;
}

/** O que este workspace tem para exportar, por família. */
export type ExportableInventory = Record<ExportSection, readonly ExportableItem[]>;

/** O que foi marcado, família por família — o pedido inteiro numa estrutura só. */
export type ExportPicked = Record<ExportSection, readonly string[]>;

/**
 * POR QUE uma peça entrou sem ter sido marcada — um motivo NOMEADO.
 *
 * O servidor manda o fato ("esta regra dispara neste horário") e não a frase:
 * a explicação aparece numa tela que fala sete idiomas, e uma frase pronta
 * vinda do back sairia em português no meio de um painel em alemão. As palavras
 * são escolhidas em `blueprint.export.reason.*`.
 */
export const DRAG_REASONS = [
	"pipeline_needs_entity",
	"report_needs_artifact",
	"artifact_reads_entity",
	"rule_fires_on_entity",
	"rule_fires_on_pipeline",
	"rule_runs_artifact",
	"rule_writes_entity",
] as const;

export type DragReason = (typeof DRAG_REASONS)[number];

/** Uma peça que entrou sem ter sido marcada, porque a marcada não vive sem ela. */
export interface DraggedPiece {
	readonly section: ExportSection;
	readonly code: string;
	/** O nome da peça que ENTROU. */
	readonly name: string;
	readonly reason: DragReason;
	/** O nome da peça que PRECISOU dela. */
	readonly by: string;
}

/**
 * O INVENTÁRIO: o que existe para escolher, por família.
 *
 * Uma consulta ao servidor e não seis consultas às features (estrutura, funis,
 * agentes, automações, artefatos, relatórios), porque a lista tem de ser a do
 * EXPORTADOR: ele já filtra o atendente que não viaja, e uma caixinha que,
 * marcada, não produz nada no documento é pior do que caixinha nenhuma. Seis
 * caches costurados aqui divergiriam do arquivo no primeiro filtro que mudasse
 * de um lado só.
 */
export function useExportable() {
	return useQuery({
		queryKey: ["blueprints", "exportable"],
		queryFn: async (): Promise<ExportableInventory> =>
			request<ExportableInventory>("/v3/blueprints/exportable"),
		staleTime: 60_000,
	});
}

/**
 * The tenant's own structure, written back out as a document.
 *
 * A mutation rather than a query although it only reads: it runs over the pieces
 * somebody picked, and a query keyed on that selection would refetch a whole
 * workspace every time a checkbox moved.
 *
 * Uma peça por família (26/08): `entities=` era o único botão e o grafo
 * arrastava o resto. Só as famílias com algo marcado viajam no endereço — um
 * parâmetro vazio é um parâmetro que o servidor recusa.
 */
export function useExportBlueprint() {
	return useMutation({
		mutationFn: async (input: {
			picked: ExportPicked;
			code?: string;
			name?: string;
			seedEntities?: readonly string[];
		}) => {
			const params = new URLSearchParams();
			for (const section of EXPORT_SECTIONS) {
				const codes = input.picked[section];
				if (codes.length > 0) params.set(section, codes.join(","));
			}
			if (input.seedEntities?.length) params.set("seedEntities", input.seedEntities.join(","));
			if (input.code) params.set("code", input.code);
			if (input.name) params.set("name", input.name);
			return request<{
				blueprint: Record<string, unknown>;
				missing: string[];
				dragged: DraggedPiece[];
				/** As telas exportadas que o servidor aceita como catálogo (sem gente, sem funil). */
				catalogue?: string[];
			}>(`/v3/blueprints/export?${params.toString()}`, { timeoutMs: 60_000 });
		},
	});
}

/** The register: which documents were applied here, and what each one did. */
export function useAppliedBlueprints() {
	return useQuery({
		queryKey: ["blueprints", "applied"],
		queryFn: async (): Promise<AppliedBlueprint[]> => {
			const res = await request<{ applied: AppliedBlueprint[] }>("/v3/blueprints/applied");
			return res.applied;
		},
		staleTime: 30_000,
	});
}
